import React, { PropsWithChildren, ReactNode, useCallback, useRef, useState } from 'react';
import classes from './KnowledgePanel.module.scss';
import cx from 'classnames';
import { styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import Typography from 'new/elements/Typography';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { Close } from '@mui/icons-material';
import { createPortal } from 'react-dom';

export interface KnowledgePanelProps {
  fullWidth?: boolean;
  hideLine?: boolean;
  enterDelay?: number;
  leaveDelay?: number;
  trigger?: 'hover' | 'click';
  alwaysShowUnderline?: boolean;
  panelMargin?: number;
  preventBackdropClose?: boolean;
  knowledgeContent: string | ReactNode;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    maxWidth: '600px',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[4],
    fontSize: 11,
  },
}));

export const KnowledgePanel = (props: PropsWithChildren<KnowledgePanelProps>) => {
  const {
    fullWidth,
    trigger = 'click',
    enterDelay = 1,
    leaveDelay = 200,
    alwaysShowUnderline,
    preventBackdropClose,
    hideLine = false,
  } = props;
  const [hovering, setHovering] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const timeoutOpen = useRef(null);
  const containerRef = useRef(null);
  const { children, knowledgeContent } = props;

  const onMouseEnter = useCallback((e) => {
    if (trigger === 'hover') {
      clearTimeout(timeoutOpen.current);
      timeoutOpen.current = setTimeout(() => {
        setAnchorEl(containerRef.current);
      }, enterDelay);
    }
    setHovering(true);
  }, []);

  const onMouseLeave = useCallback((e) => {
    if (trigger === 'hover') {
      clearTimeout(timeoutOpen.current);
      timeoutOpen.current = setTimeout(() => {
        setAnchorEl(null);
      }, leaveDelay);
    }
    setHovering(false);
  }, []);

  const handleOpen = (event) => {
    event.stopPropagation();
    if (trigger !== 'click') {
      return;
    }
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = (e, reason) => {
    if (preventBackdropClose && reason === 'backdropClick') {
      return;
    }
    setAnchorEl(null);
  };

  const handleCloseOnClick = (e) => {
    return handleClose(e, 'clickClose');
  };

  const open = !!anchorEl;

  const Content = (() => {
    if (typeof knowledgeContent === 'string') {
      return <Typography sx={{ p: 2 }}>{knowledgeContent}</Typography>;
    }
    return knowledgeContent;
  })();

  return (
    <>
      <LightTooltip
        open={open}
        componentsProps={{
          popper: {
            onMouseEnter,
            onMouseLeave,
          },
        }}
        title={
          <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={cx(classes.popoverContent, {
              [classes.popoverContent_closable]: preventBackdropClose,
            })}
          >
            {!!preventBackdropClose && (
              <IconButton onClick={handleCloseOnClick} className={classes.closeButton}>
                <Close />
              </IconButton>
            )}
            {Content}
          </div>
        }
        placement="bottom-start"
      >
        <div
          ref={containerRef}
          onClick={handleOpen}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={cx(classes.wrapper, {
            [classes.wrapper_show]: hovering || open,
          })}
        >
          {children}
          {alwaysShowUnderline && !hideLine && (
            <div className={cx(classes.line_position, classes.light_line)} />
          )}
          {!hideLine ? <div className={cx(classes.line_position, classes.line)} /> : null}
        </div>
      </LightTooltip>
      {trigger === 'click' && open && !preventBackdropClose
        ? createPortal(
            <div
              className={classes.backdrop}
              onClick={() => {
                setAnchorEl(null);
              }}
            />,
            document.body,
          )
        : null}
    </>
  );
};
